import React, { useContext, useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    IconButton,
    InputAdornment,
    Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Layout from "components/structure/Layout";
import CheckPendingTranslationService from '../../../services/CheckPendingTranslationService';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { SnackbarContext } from "context/snackBarContext";
import CheckboxTick from "assets/CheckboxTick.svg";
import CheckboxUntick from "assets/CheckboxUntick.svg";
import { useNavigate } from 'react-router-dom';
import CommonService from '../../../services/CommonService';

const CheckPendingTranslation = () => {
    // Get user from sessionStorage
    const userData = sessionStorage.getItem('BAT_USER');
    const user = userData ? JSON.parse(userData) : null;
    const isLocalUser = user?.userAttribute === "Local";
    const userCountryCode = user?.countryCode; // Adjust this if your user object uses a different key

    const [filters, setFilters] = useState({
        country: '',
        cropYear: '',
        cropStage: [],
        module: [],
        subModule: []
    });

    const [searchTerm, setSearchTerm] = useState('');
    const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);
    const [pendingTranslation, setPendingTranslation] = useState(false);
    const [countries, setCountries] = useState([]);
    const [cropYears, setCropYears] = useState([]);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [cropStages, setCropStages] = useState([]);
    const [modules, setModules] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [subModules, setSubModules] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const loadCountries = async () => {
            try {
                const response = await CommonService.fetchCountries();
                const countriesData = response.data.response || [];
                console.log('countriesData', countriesData);
                setCountries(countriesData);

                // If user is Local, set their country as default and disable dropdown
                if (isLocalUser && userCountryCode) {
                    setFilters(prev => ({
                        ...prev,
                        country: userCountryCode
                    }));
                } else if (countriesData.length > 0) {
                    setFilters(prev => ({
                        ...prev,
                        country: countriesData[0].countryCode
                    }));
                }
            } catch (error) {
                setCountries([]);
                setSeverity("error");
                const errorMessage = error.response?.data?.message || error.errorMessage || "Error fetching countries";
                setMessage(errorMessage);
                setOpenAlert(true);
            }
        };
        loadCountries();
    }, []);

    useEffect(() => {
        const loadCropYears = async () => {
            if (filters.country) {
                try {
                    const config = {
                        headers: {
                            apiCountryCode: filters.country
                        }
                    };
                    const response = await CheckPendingTranslationService.getCropYears(config);
                    const years = response.data.response || [];
                    setCropYears(years);

                    // Find and set the current crop year
                    const currentCropYear = years.find(year => year.isCurrentCropYear);
                    if (currentCropYear) {
                        setFilters(prev => ({
                            ...prev,
                            cropYear: currentCropYear.cropYear
                        }));
                    }
                } catch (error) {
                    console.error('Error fetching crop years:', error);
                    setCropYears([]);
                    setSeverity("error");
                    const errorMessage = error.response?.data?.message || error.errorMessage;
                    setMessage(errorMessage);
                    setOpenAlert(true);
                }
            } else {
                setCropYears([]);
            }
        };
        loadCropYears();
    }, [filters.country]);

    useEffect(() => {
        const loadCropStages = async () => {
            if (filters.country) {
                try {
                    const config = {
                        headers: {
                            apiCountryCode: filters.country
                        }
                    };
                    console.log('crop stage API config:', config);
                    const response = await CheckPendingTranslationService.getCropStage(config);
                    const stages = response.data.response || [];
                    setCropStages(stages);
                    setFilters(prev => ({
                        ...prev,
                        cropStage: stages.map(stage => stage.cropStageUid)
                    }));
                } catch (error) {
                    console.error('Error fetching crop stages:', error);
                    setCropStages([]);
                    setSeverity("error");
                    const errorMessage = error.response?.data?.message || error.errorMessage;
                    setMessage(errorMessage);
                    setOpenAlert(true);
                }
            } else {
                setCropStages([]);
            }
        };
        loadCropStages();
    }, [filters.country]);

    useEffect(() => {
        const loadModules = async () => {
            if (filters.country && filters.cropStage.length > 0) {
                try {
                    const config = {
                        headers: {
                            apiCountryCode: filters.country
                        }
                    };
                    console.log('Module API config:', config);
                    const response = await CheckPendingTranslationService.getModule(config, filters.cropStage);
                    const modulesData = response.data.response || [];
                    setModules(modulesData);
                    setFilters(prev => ({
                        ...prev,
                        module: modulesData.map(module => module.moduleId)
                    }));
                } catch (error) {
                    console.error('Error fetching modules:', error);
                    setModules([]);
                }
            } else {
                setModules([]);
            }
        };
        loadModules();
    }, [filters.country, filters.cropStage]);

    useEffect(() => {
        const loadSubModules = async () => {
            if (filters.country && filters.module.length > 0) {
                try {
                    const config = {
                        headers: {
                            apiCountryCode: filters.country
                        }
                    };
                    const response = await CheckPendingTranslationService.getSubModule(config, filters.module);
                    const subModulesData = response.data.response || [];
                    setSubModules(subModulesData);
                    setFilters(prev => ({
                        ...prev,
                        subModule: subModulesData.map(module => module.moduleId)
                    }));
                } catch (error) {
                    console.error('Error fetching submodules:', error);
                    setSubModules([]);

                }
            } else {
                setSubModules([]);
            }
        };
        loadSubModules();
    }, [filters.country, filters.module]);

    useEffect(() => {
        const shouldFetchData = () => {
            return (
                filters.country &&
                filters.cropYear &&
                filters.cropStage.length > 0 &&
                isInitialLoadComplete
            );
        };

        if (shouldFetchData()) {
            fetchTranslationData();
        }
    }, [filters, pendingTranslation, searchTerm, fromDate, toDate, isInitialLoadComplete]);

    useEffect(() => {
        const checkInitialLoad = () => {
            if (
                countries.length > 0 &&
                cropYears.length > 0 &&
                cropStages.length > 0 &&
                modules.length > 0 &&
                subModules.length > 0
            ) {
                setIsInitialLoadComplete(true);
            }
        };

        checkInitialLoad();
    }, [countries, cropYears, cropStages, modules, subModules]);

    const fetchTranslationData = async () => {
        if (isDataLoading) return;

        try {
            setIsDataLoading(true);
            const config = {
                headers: {
                    apiCountryCode: filters.country
                }
            };

            const params = {
                cropYear: filters.cropYear || null,
                cropStageIds: filters.cropStage.length > 0 ? filters.cropStage : null,
                moduleIds: filters.module.length > 0 ? filters.module : [],
                subModuleIds: filters.subModule.length > 0 ? filters.subModule : [],
                keyword: searchTerm || null,
                fromDate: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : null,
                toDate: toDate ? dayjs(toDate).format('YYYY-MM-DD') : null
            };

            if (pendingTranslation) {
                params.isPending = true;
            }

            Object.keys(params).forEach(key => {
                if (params[key] === null) {
                    delete params[key];
                }
            });

            const response = await CheckPendingTranslationService.getTranslationData(params, config);
            const responseData = response.data.response || [];
            setTableData(responseData);
        } catch (error) {
            setSeverity("error");
            const errorMessage = error.response?.data?.message || error.response?.data?.errorMessage || "No Records Found";
            setMessage(errorMessage);
            setOpenAlert(true);
            setTableData([]); // Set empty array on error
        } finally {
            setIsDataLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        if (field === 'cropStage') {
            // Handle "Select All" for crop stages
            if (value.includes('select-all')) {
                if (filters.cropStage.length === cropStages.length) {
                    // If all are selected, deselect all
                    setFilters(prev => ({
                        ...prev,
                        [field]: [],
                        module: [],
                        subModule: []
                    }));
                } else {
                    // Select all
                    setFilters(prev => ({
                        ...prev,
                        [field]: cropStages.map(stage => stage.cropStageUid),
                        module: [],
                        subModule: []
                    }));
                }
            } else {
                // Normal selection
                setFilters(prev => ({
                    ...prev,
                    [field]: value,
                    module: [],
                    subModule: []
                }));
            }
        } else if (field === 'module') {
            // Handle "Select All" for modules
            if (value.includes('select-all')) {
                if (filters.module.length === modules.length) {
                    // If all are selected, deselect all
                    setFilters(prev => ({
                        ...prev,
                        [field]: []
                    }));
                } else {
                    // Select all
                    setFilters(prev => ({
                        ...prev,
                        [field]: modules.map(module => module.moduleId)
                    }));
                }
            } else {
                // Normal selection
                setFilters(prev => ({
                    ...prev,
                    [field]: value
                }));
            }
        } else if (field === 'subModule') {
            // Handle "Select All" for sub modules
            if (value.includes('select-all')) {
                if (filters.subModule.length === subModules.length) {
                    // If all are selected, deselect all
                    setFilters(prev => ({
                        ...prev,
                        [field]: []
                    }));
                } else {
                    // Select all
                    setFilters(prev => ({
                        ...prev,
                        [field]: subModules.map(module => module.moduleId)
                    }));
                }
            } else {
                // Normal selection
                setFilters(prev => ({
                    ...prev,
                    [field]: value
                }));
            }
        } else {
            setFilters(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleFromDateChange = (newValue) => {
        if (toDate && newValue > toDate) {
            setSeverity("error");
            setMessage("From date cannot be after To date");
            setOpenAlert(true);
            return;
        }
        setFromDate(newValue);
    };

    const handleToDateChange = (newValue) => {
        if (fromDate && newValue < fromDate) {
            setSeverity("error");
            setMessage("To date cannot be before From date");
            setOpenAlert(true);
            return;
        }
        setToDate(newValue);
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const renderTableRows = (data) => {
        return data.flatMap((item, index) => {
            const rows = [{
                category: item.category,
                english: item.englishText,
                portuguese: item.localText,
                lastUpdated: item.lastUpdatedDate ? dayjs(item.lastUpdatedDate).format('DD/MM/YYYY') : '-',
                notificationDate: item.notificationDate ? dayjs(item.notificationDate).format('DD/MM/YYYY') : '-',
                status: item.status || '-'
            }];

            if (item.children) {
                rows.push(...renderTableRows(item.children));
            }

            return rows;
        });
    };

    const handleExportToExcel = () => {
        // Add UTF-8 BOM
        const BOM = '\uFEFF';

        // Convert table data to CSV format
        const headers = [
            'Category',
            'English',
            getLanguageName(filters.country),
            'Last Updated Date',
            'Notification Sent Date',
            'Pending/Updated'
        ];

        const csvData = renderTableRows(tableData).map(row => [
            row.category,
            row.english,
            row.portuguese || '-',
            row.lastUpdated,
            row.notificationDate,
            row.status
        ]);

        // Create CSV content with BOM and proper escaping
        const csvContent = BOM + [
            headers.join(','),
            ...csvData.map(row => row.map(cell => {
                // Properly escape cells containing commas, quotes, or special characters
                const escaped = String(cell).replace(/"/g, '""');
                return `"${escaped}"`;
            }).join(','))
        ].join('\n');

        // Create blob with UTF-8 encoding
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `translation_report_${dayjs().format('YYYY-MM-DD')}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url); // Clean up the URL object
    };

    const getLanguageName = (countryCode) => {
        const country = countries.find(c => c.countryCode === countryCode);
        return country?.localLanguage || '';
    };

    return (
        <Layout title="Translation Report">
            <Box sx={{ p: 1, backgroundColor: '#f5f5f5', minHeight: '80vh' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h5" sx={{
                        fontSize: '20px',
                        fontFamily: 'Montserrat',
                        fontWeight: 600,
                        color: '#737D86'
                    }}>
                        Translation Report
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>

                        <Button
                            variant="contained"
                            onClick={handleExportToExcel}
                            sx={{
                                fontFamily: 'Montserrat',
                                backgroundColor: '#112A63',
                                borderRadius: "12px",
                                '&:hover': {
                                    backgroundColor: '#112A63'
                                }
                            }}
                        >
                            EXPORT TO EXCEL
                        </Button>
                    </Box>
                </Box>

                {/* White Box Container */}
                <Paper sx={{ p: 3, pb: 0.1, backgroundColor: 'white', borderRadius: '10px', boxShadow: 2, mb: 10 }}>
                    {/* Filters Row 1 */}
                    <Grid container spacing={4} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Country</InputLabel>
                                <Select
                                    value={filters.country}
                                    label="Country"
                                    onChange={(e) => handleFilterChange('country', e.target.value)}
                                    disabled={isLocalUser}
                                    sx={{
                                        '&.Mui-disabled .MuiSelect-select': {
                                            fontWeight: 400, // Normal font weight
                                            color: 'rgba(0,0,0,0.38)', // Default MUI disabled color
                                        }
                                    }}
                                >
                                    {countries.map((country) => (
                                        <MenuItem key={country.countryId} value={country.countryCode}>
                                            {country.countryName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Crop Year</InputLabel>
                                <Select
                                    value={filters.cropYear}
                                    label="Crop Year"
                                    onChange={(e) => handleFilterChange('cropYear', e.target.value)}
                                >

                                    {cropYears.map((year) => (
                                        <MenuItem key={year.cropYearUid} value={year.cropYear}>
                                            {year.cropYearName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="From send notification date"
                                    value={fromDate}
                                    onChange={handleFromDateChange}
                                    maxDate={toDate}
                                    format="DD-MM-YYYY"

                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            InputLabelProps: {
                                                shrink: true,
                                                sx: {
                                                    backgroundColor: "white",
                                                    paddingX: '4px'
                                                },
                                            },
                                            sx: {
                                                '& .MuiOutlinedInput-notchedoutlined': {
                                                    top: 0
                                                },
                                                '& .MuiInputLabel-outlined': {
                                                    zIndex: 1
                                                }
                                            }

                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="To send notification date"
                                    value={toDate}
                                    onChange={handleToDateChange}
                                    minDate={fromDate}
                                    format="DD-MM-YYYY"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            variant: "outlined", // Try "filled" or "standard"
                                            InputLabelProps: {
                                                shrink: true,
                                                sx: {
                                                    backgroundColor: "white",
                                                    paddingX: '4px'
                                                },
                                            },
                                            sx: {
                                                '& .MuiOutlinedInput-notchedoutlined': {
                                                    top: 0
                                                },
                                                '& .MuiInputLabel-outlined': {
                                                    zIndex: 1
                                                }
                                            }

                                        },
                                    }}
                                />
                            </LocalizationProvider>

                        </Grid>

                        <Grid item xs={12} sm={12} md={2.4} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Montserrat', fontWeight: 500, }}>
                                    Pending Translation
                                </Typography>
                                <Checkbox
                                    checked={pendingTranslation}
                                    onChange={(e) => setPendingTranslation(e.target.checked)}
                                    size="small"
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Filters Row 2 */}
                    <Grid container spacing={4} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Crop Stage</InputLabel>
                                <Select
                                    multiple
                                    value={filters.cropStage}
                                    label="Crop Stage"
                                    onChange={(e) => handleFilterChange('cropStage', e.target.value)}
                                    renderValue={(selected) => {
                                        return selected
                                            .map(stageId => cropStages.find(stage => stage.cropStageUid === stageId)?.cropStageName)
                                            .join(', ');
                                    }}
                                >
                                    <MenuItem value="select-all">
                                        <Checkbox
                                            checked={filters.cropStage.length === cropStages.length}
                                            indeterminate={filters.cropStage.length > 0 && filters.cropStage.length < cropStages.length}
                                            icon={<img src={CheckboxUntick} alt="Unchecked" />}
                                            checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                                        />
                                        Select All
                                    </MenuItem>
                                    {cropStages.map((stage) => (
                                        <MenuItem key={stage.cropStageUid} value={stage.cropStageUid}>
                                            <Checkbox
                                                checked={filters.cropStage.includes(stage.cropStageUid)}
                                                icon={<img src={CheckboxUntick} alt="Unchecked" />}
                                                checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                                            />
                                            {stage.cropStageName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Module</InputLabel>
                                <Select
                                    multiple
                                    value={filters.module}
                                    label="Module"
                                    onChange={(e) => handleFilterChange('module', e.target.value)}
                                    renderValue={(selected) => {
                                        return selected
                                            .map(moduleId => modules.find(module => module.moduleId === moduleId)?.moduleName)
                                            .join(', ');
                                    }}
                                >
                                    <MenuItem value="select-all">
                                        <Checkbox
                                            checked={filters.module.length === modules.length}
                                            indeterminate={filters.module.length > 0 && filters.module.length < modules.length}
                                            icon={<img src={CheckboxUntick} alt="Unchecked" />}
                                            checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                                        />
                                        Select All
                                    </MenuItem>
                                    {modules.map((module) => (
                                        <MenuItem key={module.moduleId} value={module.moduleId}>
                                            <Checkbox
                                                checked={filters.module.includes(module.moduleId)}
                                                icon={<img src={CheckboxUntick} alt="Unchecked" />}
                                                checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                                            />
                                            {module.moduleName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Sub Module</InputLabel>
                                <Select
                                    multiple
                                    value={filters.subModule}
                                    label="Sub Module"
                                    onChange={(e) => handleFilterChange('subModule', e.target.value)}
                                    renderValue={(selected) => {
                                        return selected
                                            .map(moduleId => subModules.find(module => module.moduleId === moduleId)?.moduleName)
                                            .join(', ');
                                    }}
                                >
                                    <MenuItem value="select-all">
                                        <Checkbox
                                            checked={filters.subModule.length === subModules.length}
                                            indeterminate={filters.subModule.length > 0 && filters.subModule.length < subModules.length}
                                            icon={<img src={CheckboxUntick} alt="Unchecked" />}
                                            checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                                        />
                                        Select All
                                    </MenuItem>
                                    {subModules.map((module) => (
                                        <MenuItem key={module.moduleId} value={module.moduleId}>
                                            <Checkbox
                                                checked={filters.subModule.includes(module.moduleId)}
                                                icon={<img src={CheckboxUntick} alt="Unchecked" />}
                                                checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                                            />
                                            {module.moduleName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4.7}>
                            <TextField
                                size="small"
                                placeholder="Search"
                                fullWidth
                                value={searchTerm}
                                onChange={handleSearch}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small">
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Table */}
                    <TableContainer
                        component={Paper}
                        sx={{
                            boxShadow: 2,
                            borderRadius: '18px',
                            mb: 2,
                            maxWidth: '100%',
                            maxHeight: "60vh",
                            overflow: 'auto'  // Enable horizontal scrolling
                        }}
                    >
                        <Table stickyHeader sx={{ minWidth: 1200 }}>  {/* Set minimum width to ensure columns maintain their widths */}
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{
                                        backgroundColor: '#112A63',
                                        color: 'white',
                                        fontFamily: 'Montserrat',
                                        fontWeight: 600,
                                        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                                        minWidth: '120px',
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word'
                                    }}>Category</TableCell>
                                    <TableCell sx={{
                                        backgroundColor: '#112A63',
                                        color: 'white',
                                        fontFamily: 'Montserrat',
                                        fontWeight: 600,
                                        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                                        minWidth: '360px',
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word'
                                    }}>English</TableCell>
                                    <TableCell sx={{
                                        backgroundColor: '#112A63',
                                        color: 'white',
                                        fontFamily: 'Montserrat',
                                        fontWeight: 600,
                                        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                                        minWidth: '360px',
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word'
                                    }}>{getLanguageName(filters.country)}</TableCell>
                                    <TableCell sx={{
                                        backgroundColor: '#112A63',
                                        color: 'white',
                                        fontFamily: 'Montserrat',
                                        fontWeight: 600,
                                        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                                        minWidth: '240px',
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word'
                                    }}>Last Updated Date</TableCell>
                                    <TableCell sx={{
                                        backgroundColor: '#112A63',
                                        color: 'white',
                                        fontFamily: 'Montserrat',
                                        fontWeight: 600,
                                        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                                        minWidth: '240px',
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word'
                                    }}>Notification Sent Date</TableCell>
                                    <TableCell sx={{
                                        backgroundColor: '#112A63',
                                        color: 'white',
                                        fontFamily: 'Montserrat',
                                        fontWeight: 600,
                                        minWidth: '240px',
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word'
                                    }}>Pending / Updated</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableData?.length > 0 ? (
                                    renderTableRows(tableData).map((row, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{ backgroundColor: '#ffffff' }}
                                        >
                                            <TableCell sx={{
                                                fontWeight: 'medium',
                                                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                                                minWidth: '120px',
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word'
                                            }}>{row.category}</TableCell>
                                            <TableCell sx={{
                                                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                                                minWidth: '360px',
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word'
                                            }}>{row.english}</TableCell>
                                            <TableCell sx={{
                                                minWidth: '360px',
                                                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word'
                                            }}>{row.portuguese || '-'}</TableCell>
                                            <TableCell sx={{
                                                minWidth: '240px',
                                                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word'
                                            }}>{row.lastUpdated}</TableCell>
                                            <TableCell sx={{
                                                minWidth: '240px',
                                                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word'
                                            }}>{row.notificationDate}</TableCell>
                                            <TableCell sx={{
                                                minWidth: '240px',
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word'
                                            }}>{row.status}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} style={{ textAlign: 'start', fontFamily: 'Montserrat', fontWeight: 500, fontSize: '14px', color: '#666' }}>
                                            No data available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Layout>
    )
}

export default CheckPendingTranslation;